import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  role: string;
  permissions: Record<string, { enabled: boolean; features: Record<string, boolean> }>;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = "td_token";

// Dev mock — bypasses login when no backend is available
const DEV_MOCK_USER: AuthUser = {
  id: 1,
  username: "nestor.aguirre",
  email: "nestor.aguirre@miglobal.com.mx",
  role: "manager",
  permissions: {
    dashboard: { enabled: true, features: { view: true } },
    immex: { enabled: true, features: { view: true, add: true, edit: true } },
    shipments: { enabled: true, features: { view: true, add: true, edit: true, delete: true } },
    workplans: { enabled: true, features: { view: true, add: true, edit: true, delete: true } },
    documents: {
      enabled: true,
      features: { view: true, upload: true, review: true, delete: true },
    },
    fees: { enabled: true, features: { view: true, add: true, edit: true, delete: true } },
    metrics: { enabled: true, features: { view: true, log: true } },
    reports: { enabled: true, features: { view: true, export: true } },
  },
};
const IS_DEV_MOCK = import.meta.env.DEV && import.meta.env.VITE_DEV_MOCK === "true";

function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    return JSON.parse(atob(token.split(".")[1] ?? "")) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function isTokenValid(token: string): boolean {
  const payload = decodeJwtPayload(token);
  const exp = payload["exp"];
  if (typeof exp !== "number") return false;
  return exp * 1000 > Date.now();
}

function userFromPayload(payload: Record<string, unknown>): AuthUser | null {
  if (
    typeof payload["id"] !== "number" ||
    typeof payload["username"] !== "string" ||
    typeof payload["email"] !== "string" ||
    typeof payload["role"] !== "string"
  ) {
    return null;
  }
  const permissions =
    payload["permissions"] != null &&
    typeof payload["permissions"] === "object" &&
    !Array.isArray(payload["permissions"])
      ? (payload["permissions"] as AuthUser["permissions"])
      : {};
  return {
    id: payload["id"],
    username: payload["username"],
    email: payload["email"],
    role: payload["role"],
    permissions,
  };
}

interface LoginResponse {
  token: string;
  user: AuthUser;
}

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // On mount: restore session from localStorage (or use dev mock)
  useEffect(() => {
    if (IS_DEV_MOCK) {
      setUser(DEV_MOCK_USER);
      setToken("dev-mock-token");
      setIsLoading(false);
      return;
    }

    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored != null && isTokenValid(stored)) {
      const payload = decodeJwtPayload(stored);
      const restored = userFromPayload(payload);
      if (restored != null) {
        setToken(stored);
        setUser(restored);
        setIsLoading(false);
        return;
      }
    }
    // Token missing or invalid — clear and finish loading
    localStorage.removeItem(TOKEN_KEY);
    setIsLoading(false);
  }, []);

  // Listen for 401 events dispatched by api.ts — log out via React state, no hard nav
  useEffect(() => {
    const handle = (): void => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener("td:unauthorized", handle);
    return () => window.removeEventListener("td:unauthorized", handle);
  }, []);

  const storeSession = (res: LoginResponse): void => {
    localStorage.setItem(TOKEN_KEY, res.token);
    setToken(res.token);
    setUser(res.user);
  };

  const login = async (email: string, password: string): Promise<void> => {
    const res = await api.post<LoginResponse>("/auth/login", { email, password });
    storeSession(res);
  };

  const logout = (): void => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    // Best-effort server-side logout; no need to await
    void api.post("/auth/logout").catch(() => undefined);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx == null) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
